import { useEffect, useState } from "react"

/**
 * Tracks the content width of a DOM element via ResizeObserver.
 * Lets components respond to their actual rendered size (container-query style)
 * without relying on Tailwind container query classes — which Vite's default
 * cssTarget (chrome87/firefox78/safari14) can strip from production builds.
 *
 * `ref` is a callback ref held in state, so elements that mount later — behind a
 * loading gate, say — still get measured; a plain useRef would have been null when
 * the mount effect ran and never observed anything.
 */
export function useElementWidth<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null)
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    if (!element) return
    // Capture initial width synchronously
    setWidth(element.getBoundingClientRect().width)
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return { ref: setElement, width }
}
